import { useMemo } from 'react';
import { useTranslation } from '../../../i18n/useTranslation';
import { useToast } from '../../../stores/toastStore';
import { Inline } from '../../atoms/Inline/Inline';
import { Stack } from '../../atoms/Stack/Stack';
import { Text } from '../../atoms/Typography/Text';
import { LivestockForm } from '../LivestockForm';
import type { LivestockFormPrefill, LivestockFormSuccessOptions } from '../LivestockForm';
import type { OwnIdentification } from './IdentifyResultsStep';
import { composeScientificName } from './scientificName';

/** The parts of an identification candidate (src/validators/livestockIdentification.validator.ts) this step uses. */
export interface IdentifiedCandidate {
  category: 'coral' | 'fish' | 'invertebrate' | 'other';
  taxonomy: { family?: string; genus?: string; species?: string };
  commonNames: string[];
  confidence: 'high' | 'medium' | 'low';
  featureExplanation: string;
}

export interface IdentifyPrefillFormStepProps {
  aquariumId: string;
  /** The accepted AI suggestion — or, instead, the user's own name (ownIdentification). */
  candidate?: IdentifiedCandidate | null;
  /** The name the user gave the animal themselves ("Different animal — name it yourself"). */
  ownIdentification?: OwnIdentification | null;
  /** Temporary upload of the identification photo; omitted when there is none. */
  tempPhotoKey?: string;
  isOpen: boolean;
  /** Called after the livestock item is saved, with the photo choice and the saved names. */
  onSaved: (created: { id: string } | undefined, options: LivestockFormSuccessOptions) => void | Promise<void>;
  /** Cancel creates nothing; the caller discards the temporary photo. */
  onCancel: () => void;
}

/** The scientific name at the depth the AI could actually recognise — never deeper. */
export function scientificNameOf(taxonomy: IdentifiedCandidate['taxonomy']): string {
  // The genus never twice, even when the AI put the binomial into `species`.
  return composeScientificName(taxonomy);
}

/**
 * Step 3 of "Identify from photo" (epic #1459): the existing LivestockForm, pre-filled from
 * the accepted candidate (or the user's own name) and fully editable, with the photo-attach
 * opt-out. Nothing is stored until the user saves. Rendered inside the flow's dialog (the
 * dialog title names the step), so the form is embedded rather than opening a dialog of its own.
 */
export function IdentifyPrefillFormStep({
  aquariumId,
  candidate = null,
  ownIdentification = null,
  tempPhotoKey,
  isOpen,
  onSaved,
  onCancel,
}: IdentifyPrefillFormStepProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();

  const commonName = ownIdentification?.commonName ?? candidate?.commonNames[0] ?? '';
  let scientificName = '';
  if (ownIdentification) scientificName = ownIdentification.scientificName;
  else if (candidate) scientificName = scientificNameOf(candidate.taxonomy);
  const category = ownIdentification?.category ?? candidate?.category;
  let sourceLabel = '';
  if (ownIdentification) sourceLabel = t('identify.form.ownName');
  else if (candidate) sourceLabel = t(`identify.form.confidence.${candidate.confidence}`);
  const featureExplanation = candidate?.featureExplanation ?? '';
  const isOwnName = ownIdentification !== null;

  const prefill = useMemo<LivestockFormPrefill>(
    () => ({
      commonName,
      scientificName,
      ...(category ? { category } : {}),
      notes: isOwnName
        ? t('identify.form.notesOwnName')
        : t('identify.form.notesSummary', {
            taxonomySummary: scientificName || commonName,
            confidenceLabel: sourceLabel,
            featureExplanation,
          }).trim(),
      ...(tempPhotoKey ? { tempPhotoKey } : {}),
    }),
    [commonName, scientificName, category, isOwnName, sourceLabel, featureExplanation, tempPhotoKey, t],
  );

  return (
    <LivestockForm
      aquariumId={aquariumId}
      prefill={prefill}
      isOpen={isOpen}
      // The flow's dialog is already open: render the form as this step's content,
      // never as a second dialog nested inside it.
      embedded
      // The user named the animal: fetch scientific name, category and care basics for them.
      autoLookup={isOwnName}
      onClose={onCancel}
      // Returned so the form stays disabled until the photo is attached — no second save.
      onSuccess={(created, options) => {
        showToast(t('identify.form.saveSuccess', { commonName: options?.saved?.commonName ?? commonName }), 'success');
        return onSaved(created, options ?? { attachPhoto: false });
      }}
      copy={{
        title: t('identify.form.title'),
        intro: (
          <Stack gap="2" className="identify-prefill-form__intro">
            <Text color="muted">{t('identify.form.instructions')}</Text>
            {commonName && (
              <Inline gap="2" wrap>
                <Text>{commonName}</Text>
                {scientificName && <Text color="muted">{scientificName}</Text>}
                <Text color="muted">{sourceLabel}</Text>
              </Inline>
            )}
          </Stack>
        ),
        submitLabel: t('identify.form.save'),
        cancelLabel: t('identify.form.cancel'),
        saveError: t('identify.form.error.save'),
        attachPhotoLabel: t('identify.form.attachPhoto'),
        attachPhotoHint: t('identify.form.attachPhotoHint'),
      }}
    />
  );
}

export default IdentifyPrefillFormStep;
